import { useMemo } from 'react';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { activeSlotMenuFor } from '@features/booking/utils/activeSlotMenu';
import { computeSlotFee } from '@features/booking/utils/slotFee';
import { roundMoney } from '@shared/utils/money';

export function usePaymentFee() {
  const booking = useBooking();
  const restaurant = useRestaurant();

  const displayCurrency = restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';

  const selectedSlot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;

  const selectedMenu = activeSlotMenuFor(booking, restaurant.publicSearchMode);

  const slotType = selectedSlot?.type ?? booking.guaranteeType ?? 'traditional';

  const feeBreakdown = useMemo(() => {
    // One lookup decides the branch, so the result is never null.
    const slotFee = computeSlotFee(booking, restaurant);
    if (slotFee) return slotFee;

    // WL flow or no slot: fee comes from guarantee rules API (booking.feeDeposit)
    const wlFee = booking.feeDeposit ?? 0;
    const gt = booking.guaranteeType;
    const wlType = (gt === 'deposit' || gt === 'no_show') ? gt : null;
    const wlSalesTaxPercent = restaurant.processingSalesTax ?? 0;
    const wlSalesTax = wlSalesTaxPercent > 0 ? roundMoney(wlFee * wlSalesTaxPercent / 100) : 0;
    return {
      guaranteeType: wlType,
      guaranteeSource: wlFee > 0 ? ('slot' as const) : null,
      baseFee: wlFee,
      perPerson: false,
      gratuity: 0,
      gratuityPercent: 0,
      chargesGratuity: false,
      menuTax: 0,
      salesTax: wlSalesTax,
      salesTaxPercent: wlSalesTaxPercent,
      serviceFee: 0,
      serviceFeePercent: 0,
      pairingsFee: 0,
      total: roundMoney(wlFee + wlSalesTax),
      hasBreakdown: wlFee > 0,
      currency: displayCurrency,
      isUSD: !!restaurant.settleUsd,
    };
  }, [booking, restaurant, displayCurrency]);

  const cancellationHours = selectedMenu?.cancellationHours;

  return { feeBreakdown, displayCurrency, slotType, cancellationHours };
}
